<template>
    <div class="kb-recipe-search-input">
        <form :action="formAction" method="get" ref="form">
            <input
                    @input="e => value = e.target.value"
                    name="q"
                    :value="value"
                    type="text"
                    class="kb-recipe-search-input__input"
                    :placeholder="placeholder"
                    v-on:keydown.down.prevent.stop="focusNextSuggestion"
                    v-on:keydown.up.prevent.stop="focusPreviousSuggestion"
                    v-on:keydown.enter.prevent.stop="onEnter"
                    autocomplete="off"
            >
            <a href="#" @click.prevent="onLoupeIcon">
            <kb-base-icon icon="search" class="kb-recipe-search-input__icon"></kb-base-icon>
            </a>
            <ul class="kb-recipe-search-input__suggestions" v-show="hasSuggestions">
                <li
                    v-bind:key="suggestion"
                    class="kb-recipe-search-input__suggestions__suggestion"
                    :class="{ focused: activeSuggestion === suggestion }"
                    v-for="suggestion in suggestions"
                    v-on:mouseover="focusSuggestion(suggestion)"
                    v-on:click="acceptSuggestion(suggestion)"
                >
                    {{ suggestion }}
                </li>
            </ul>
        </form>
    </div>
</template>

<script>
import BaseIcon from '@/components/Atoms/BaseIcon/BaseIcon'
import Vue from 'vue';
const debounce = require('debounce');

export default {
    name: "kb-recipe-search-input",
    props: {
        placeholder: String,
        initialValue: String,
        suggestLimit: Number,
        targetHost: String
    },
    data: function() {
        return {
            value: this.initialValue || '',
            previouslyLookedUpValue: undefined,
            suggestions: [],
            activeSuggestion: undefined,
        };
    },
    computed: {
        hasSuggestions: function() { return this.suggestions.length > 0; },
        formAction: function() {
            const host = this.targetHost || 'https://www.kochbar.de/';

            return host + (host.substr(-1) === '/' ? '' : '/') + 'rezept/suche';
        }
    },
    watch: {
        value: function() {
            if (this.value === this.previouslyLookedUpValue) {
                return;
            }

            if (this.value === '') {
                this.resetSuggestions();
                return;
            }

            this.activeSuggestion = undefined;
            this.fetchSuggestionsCached(this.value);
        }
    },
    methods: {
        cache: function() {
            if (!window.sessionStorage.getItem(this.endpoint)) {
                window.sessionStorage.setItem(this.endpoint, '{}');
            }
            return JSON.parse(window.sessionStorage.getItem(this.endpoint));
        },
        cachedSuggestionsFor: function(value) {
            return this.cache()[value] || false;
        },
        cacheNewSuggestionsFor: function(value, suggestions) {
            let cache = this.cache();
            cache[value] = suggestions;
            window.sessionStorage.setItem(this.endpoint, JSON.stringify(cache))
        },

        fetchSuggestionsCached: function(searchTerm) {
            this.previouslyLookedUpValue = searchTerm;
            const cachedSuggestions = this.cachedSuggestionsFor(searchTerm);
            if (cachedSuggestions) {
                this.applySuggestions(cachedSuggestions);
                return;
            }

            this.fetchSuggestionsFromApi(searchTerm);
        },
        fetchSuggestionsFromApi: debounce(function(searchTerm) {
            const self = this;

            fetch('https://api.kochbar.de/recipes/suggest?name=' + encodeURIComponent(searchTerm))
                .then(function(response) {
                    return response.json();
                })
                .then(function(body) {
                    const suggestions = body.result;
                    self.applySuggestions.call(self, suggestions);
                    self.cacheNewSuggestionsFor.call(self, searchTerm, suggestions);
                });
        }, 500),
        applySuggestions: function(suggestions) {
            const allSuggestions = this.sortSuggestionsByScore(suggestions)
                .map(suggestion => suggestion.text);

            if (this.suggestLimit) {
                this.suggestions = allSuggestions.splice(0, this.suggestLimit);
            } else {
                this.suggestions = allSuggestions;
            }
        },
        focusNextSuggestion: function() {
            const currentIndex = !this.activeSuggestion
                ? -1
                : this.suggestions.indexOf(this.activeSuggestion);

            let newIndex = currentIndex + 1;

            if (typeof this.suggestions[newIndex] === 'undefined') {
                newIndex = 0;
            }

            this.activeSuggestion = this.suggestions[newIndex];
        },
        focusPreviousSuggestion: function() {
            const currentIndex = !this.activeSuggestion
                ? -1
                : this.suggestions.indexOf(this.activeSuggestion);

            let newIndex = currentIndex - 1;

            if (typeof this.suggestions[newIndex] === 'undefined') {
                newIndex = this.suggestions.length - 1;
            }

            this.activeSuggestion = this.suggestions[newIndex];
        },
        focusSuggestion: function(suggestion) {
            this.activeSuggestion = suggestion;
        },
        resetSuggestions: function() {
            this.suggestions = [];
            this.activeSuggestion = undefined;
            this.hasSuggestions = false;
        },
        onEnter: function() {
            // if no suggestion is selected, submit the raw input value
            if (!this.activeSuggestion) {
                this.$refs.form.submit();
            } else {
                this.acceptSuggestion();
            }
        },
        onLoupeIcon: function () {
            this.$refs.form.submit();
        },
        acceptSuggestion: function() {
            this.previouslyLookedUpValue = this.activeSuggestion; // prevent that this change triggers a new lookup
            this.value = this.activeSuggestion;
            this.resetSuggestions();

            setTimeout(() => this.$refs.form.submit(), 0); // vue needs to update the input before submitting the form
        },
        sortSuggestionsByScore: function(suggestions) {
            return suggestions.sort(function(a, b) {
                if (a.score === b.score) return 0;
                if (a.score < b.score) return 1;
                return -1;
            });
        }
    },
}
</script>

<style lang="scss" scoped>
.kb-recipe-search-input {

    position: relative;
    display: block;

    &__input {
        position: relative;
        width: 100%;
        background-color: $cBGSecondary;
        font-family: $fontFamily;
        font-size:  $fontSizeRegular;
        color: $cTextAccent;
        border-radius: 4px;
        border: 0;
        line-height: $lineheightRegular;
        padding: $spacingXs $spacingRegular;
    }

    &__icon {
        position: absolute;
        color: $cTextAccent;
        font-size: $fontSizeRegular;
        top: $fontSizeRegular - $spacingXs;
        right: $spacingRegular;
        z-index: 2;
    }

    &__suggestions {
        position: absolute;
        top:auto;
        left:0;
        margin-top: $spacingXs;
        border: 1px solid $cBorderLight;
        border-radius: $radiusNormal;
        background-color: $cBGPrimary;
        z-index: 99999;

        &__suggestion {

            border-bottom: 1px solid $cBorderLight;
            padding: $spacingSm;
            cursor: pointer;
            font-family: $fontFamily;

            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;

            &:hover,
            &.focused {
                color: $cPrimary;
            }

            .highlight {
                color: $cPrimary;
                font-weight: bold;
            }
        }
    }
}
</style>